import { ReactNode } from 'react';

import { Typography } from '@mui/material';

import clsx from 'clsx';
import styled from 'styled-components';

import { Constants } from '@arrived/common';

import { PROPERTY_HASH_MAP } from '../Property.constants';
import { PropertyDetailsHighlight } from './PropertyDetailsHighlight';
import { PropertyDetailsTips } from './PropertyDetailsTips';

import { useMediaQueryClasses } from '../../hooks/useMediaQueryClasses';

interface PropertyDetailsSectionProps {
  hash?: PROPERTY_HASH_MAP;
  areSectionsColumns?: boolean;
  description?: ReactNode;
  hasTip?: boolean;
  isStacked?: boolean;
  title: ReactNode;
  className?: string;
  children: ReactNode;
}

const StyledPropertyDetailsSection = styled.div`
  &.property-details-section {
    &.mobile {
      background-color: ${({ theme }) => theme.palette.neutrals.white};

      .property-details-section-content {
        gap: ${({ theme }) => theme.spacing(4)};
      }
    }

    &:last-child {
      .property-details-section-content {
        margin-bottom: 0;
      }
    }

    .property-details-section-content {
      display: flex;
      flex-wrap: wrap;
      width: 100%;
      margin: ${({ theme }) => theme.spacing(3, 0, 4)};
      gap: ${({ theme }) => theme.spacing(7)};
      padding: ${({ theme }) => theme.spacing(0, 4)};

      // Ensure if MUI Grid is used, don't provide this override
      > *:not(.MuiGrid-root) {
        // The calculation value must be half that of the gap value above
        flex: 0 1 calc(50% - ${({ theme }) => theme.spacing(3.5)});
      }
    }

    &.section-columns {
      .property-details-section-content {
        flex-direction: column;
      }
    }

    &.stacked {
      .property-details-section-content {
        flex-flow: column nowrap;

        > * {
          padding-top: ${({ theme }) => theme.spacing(2.5)};
        }
      }
    }

    &.mobile {
      padding-top: ${({ theme }) => theme.spacing(2)};

      .property-details-section-content {
        padding: ${({ theme }) => theme.spacing(0, 1)};
      }

      &.section-columns {
        .property-details-section-content {
          flex-direction: column;
        }
      }

      .property-details-section-content {
        flex-direction: column;
        flex-wrap: nowrap;
        margin: ${({ theme }) => theme.spacing(0)};

        > * {
          padding: ${({ theme }) => theme.spacing(2.5, 1.5)};
        }
      }
    }
  }
`;

export const PropertyDetailsSection = ({
  hash,
  areSectionsColumns,
  children,
  description,
  hasTip = true,
  isStacked,
  title,
  className,
}: PropertyDetailsSectionProps) => {
  const mediaQueryClasses = useMediaQueryClasses();

  return (
    <StyledPropertyDetailsSection
      id={hash}
      className={clsx(
        'property-details-section',
        areSectionsColumns && 'section-columns',
        isStacked && 'stacked',
        mediaQueryClasses,
        className
      )}
    >
      {hasTip ? (
        <PropertyDetailsTips title={title} tipText={description} />
      ) : (
        <PropertyDetailsHighlight>
          <Typography variant="h5.semibold" className="property-details-section-title">
            {title}
          </Typography>
        </PropertyDetailsHighlight>
      )}
      <div className="property-details-section-content">{children}</div>
    </StyledPropertyDetailsSection>
  );
};
